import { useEffect, useCallback, Dispatch, SetStateAction } from 'react';
import { useHistory } from 'react-router-dom';
import { ITriggerPageLeave } from './types';

interface IUseBlockInternalNavigation {
  blockNavigation: boolean;
  triggerPageChange: ITriggerPageLeave;
  setTriggerPageLeave: Dispatch<SetStateAction<ITriggerPageLeave>>;
  setShowConfirmNavigationModal: Dispatch<SetStateAction<boolean>>;
}

/**
 * Custom hook to block internal navigation based on certain conditions.
 *
 * @returns {void}
 */
const useBlockInternalNavigation = ({
  blockNavigation,
  triggerPageChange,
  setTriggerPageLeave,
  setShowConfirmNavigationModal
}: IUseBlockInternalNavigation): void => {
  const history = useHistory();

  const handleGoToIntendedPage = useCallback(
    (location: string) => history.push(location),
    [history]
  );

  useEffect(() => {
    if (triggerPageChange.onConfirm) {
      handleGoToIntendedPage(triggerPageChange.path);
    }

    const unblock = history.block(
      (location: { search: string; pathname: string }) => {
        // Compares the history to the intended route
        // to check if the user is trying to navigate to the same url
        const isSameLocationPathname =
          location.pathname === history.location.pathname;
        const isSameSearchParam = location.search === history.location.search;

        const isSameUrl = isSameLocationPathname && isSameSearchParam;
        if (blockNavigation && !isSameUrl) {
          setShowConfirmNavigationModal(true);
          setTriggerPageLeave(obj => ({
            ...obj,
            path: location.pathname + location.search
          }));
          return Boolean(triggerPageChange.onConfirm);
        } else if (isSameUrl) {
          // We must return false to prevent the history from being updated
          // Returning true will update the history action to be REPLACE and trigger a rerender
          return false;
        }
      }
    );

    return () => {
      unblock();
      setTriggerPageLeave(obj => ({ ...obj, onConfirm: false }));
    };
  }, [
    blockNavigation,
    handleGoToIntendedPage,
    history,
    triggerPageChange.onConfirm,
    triggerPageChange.path,
    setShowConfirmNavigationModal,
    setTriggerPageLeave
  ]);
};

export default useBlockInternalNavigation;
